import { useLayoutEffect, useState } from "react";

import { useTheme } from "@mui/material";

import { clampAssistantPosition } from "../../../utils";
import { ASSISTANT_PANEL_OVERLAY_SELECTOR } from "../constants";
import {
  IAssistantPanelPlacementArgs,
  IAssistantPanelPosition,
} from "../types";
import { getAssistantPanelPosition } from "../utils";

export function useAssistantPanelPlacement({
  panelRef,
  visible,
  preferredPosition,
  highlights,
  dragging,
}: IAssistantPanelPlacementArgs) {
  const theme = useTheme();
  const gap = parseFloat(theme.spacing(4));
  const [position, setPosition] = useState<IAssistantPanelPosition>();

  useLayoutEffect(() => {
    if (!visible) {
      setPosition(undefined);
      return;
    }

    let frame = 0;
    let observedOverlays = new Set<HTMLElement>();
    const update = () => {
      const panel = panelRef.current;
      if (!panel) return;

      const activeOverlays = new Set<HTMLElement>();
      const overlays = Array.from(
        document.querySelectorAll<HTMLElement>(ASSISTANT_PANEL_OVERLAY_SELECTOR)
      ).flatMap((popup) => {
        if (popup.closest("[data-assistant-popup]") || panel.contains(popup)) {
          return [];
        }
        const bounds = popup.getBoundingClientRect();
        const style = getComputedStyle(popup);
        if (
          !bounds.width ||
          !bounds.height ||
          style.visibility === "hidden" ||
          style.display === "none"
        ) {
          return [];
        }
        activeOverlays.add(popup);
        return [bounds];
      });
      if (
        observedOverlays.size !== activeOverlays.size ||
        Array.from(activeOverlays).some(
          (overlay) => !observedOverlays.has(overlay)
        )
      ) {
        resizeObserver.disconnect();
        resizeObserver.observe(panel);
        activeOverlays.forEach((overlay) => resizeObserver.observe(overlay));
        observedOverlays = activeOverlays;
      }
      let next = preferredPosition;
      if (!dragging) {
        const panelBounds = panel.getBoundingClientRect();
        const viewport = {
          width: window.innerWidth,
          height: window.innerHeight,
        };
        const clampedPosition = preferredPosition
          ? clampAssistantPosition(
              preferredPosition,
              panel.getBoundingClientRect(),
              gap
            )
          : undefined;

        next = getAssistantPanelPosition(
          panelBounds,
          overlays,
          viewport,
          gap,
          clampedPosition,
          highlights
        );
      }
      setPosition((current) =>
        current?.left === next?.left && current?.top === next?.top
          ? current
          : next
      );
    };

    const scheduleUpdate = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };

    const resizeObserver = new ResizeObserver(scheduleUpdate);
    if (panelRef.current) resizeObserver.observe(panelRef.current);
    const observer = new MutationObserver(scheduleUpdate);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "aria-hidden"],
    });
    window.addEventListener("resize", scheduleUpdate);
    window.addEventListener("scroll", scheduleUpdate, true);
    update();

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      resizeObserver.disconnect();
      window.removeEventListener("resize", scheduleUpdate);
      window.removeEventListener("scroll", scheduleUpdate, true);
    };
  }, [dragging, gap, highlights, panelRef, preferredPosition, visible]);

  return position;
}
